import React from "react";
import Chip from "@mui/joy/Chip";
import Link from "@mui/joy/Link";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import { ScrollTable } from "../bids/analytics/components/AnalyticsShared";
import { formatMoneyKopecks } from "../bids/analytics/format";
import { formatUtcDateTime } from "../../common/dateTime";
import {
    AdminPromoCodeUsage,
    PromoBonusTypeLabels,
    UserPromoCodeStatus,
    UserPromoCodeStatusLabels,
    promoEntityLink,
} from "../../models/promoCode";

type Props = {
    items: AdminPromoCodeUsage[];
    /** Колонка пользователя (в карточке пользователя не нужна) */
    showUser?: boolean;
    /** Колонка кампании (во вкладке кампании не нужна) */
    showCampaign?: boolean;
};

function statusColor(status: UserPromoCodeStatus): "success" | "primary" | "neutral" | "warning" {
    switch (status) {
        case UserPromoCodeStatus.Redeemed:
            return "success";
        case UserPromoCodeStatus.Active:
            return "primary";
        case UserPromoCodeStatus.Expired:
            return "warning";
        default:
            return "neutral";
    }
}

/** Условия на момент активации: «продажа 2% · покупка −5% · депозит +3%». */
function formatConditions(usage: AdminPromoCodeUsage): string {
    const parts = [
        usage.saleCommissionPercent != null ? `продажа ${usage.saleCommissionPercent}%` : null,
        usage.purchaseDiscountPercent != null ? `покупка −${usage.purchaseDiscountPercent}%` : null,
        usage.depositBonusPercent != null ? `депозит +${usage.depositBonusPercent}%` : null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : "—";
}

function EntityCell({ usage }: { usage: AdminPromoCodeUsage }) {
    if (usage.bonusType == null || usage.entityId == null) {
        return <>—</>;
    }
    const entity = promoEntityLink(usage.bonusType, usage.entityId);
    return entity.href ? <Link href={entity.href}>{entity.label}</Link> : <>{entity.label}</>;
}

/** Таблица активаций промокодов: условия, статус, что сработало, операция, сумма и наши затраты. */
export function PromoCodeUsagesTable({ items, showUser = true, showCampaign = true }: Props) {
    return (
        <ScrollTable>
            <Table size="sm" hoverRow>
                <thead>
                    <tr>
                        {showUser && <th>Пользователь</th>}
                        {showCampaign && <th>Кампания</th>}
                        <th>Код</th>
                        <th>Статус</th>
                        <th>Активирован</th>
                        <th>Условия</th>
                        <th>Сработал</th>
                        <th>Операция</th>
                        <th style={{ textAlign: "right" }}>Сумма</th>
                        <th style={{ textAlign: "right" }}>Затраты</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((u) => (
                        <tr key={u.id}>
                            {showUser && (
                                <td>
                                    <Link href={`/app/users/${u.userId.toString()}`}>
                                        {u.userName || u.userId.toString()}
                                    </Link>
                                </td>
                            )}
                            {showCampaign && <td>{u.campaignName}</td>}
                            <td>
                                <Typography level="body-sm" fontWeight="lg">
                                    {u.code}
                                </Typography>
                            </td>
                            <td>
                                <Chip size="sm" variant="soft" color={statusColor(u.status)}>
                                    {UserPromoCodeStatusLabels[u.status]}
                                </Chip>
                            </td>
                            <td>{formatUtcDateTime(u.activatedAt)}</td>
                            <td>{formatConditions(u)}</td>
                            <td>
                                {u.bonusType != null ? PromoBonusTypeLabels[u.bonusType] : "—"}
                                {u.redeemedAt && (
                                    <Typography level="body-xs" color="neutral">
                                        {formatUtcDateTime(u.redeemedAt)}
                                    </Typography>
                                )}
                            </td>
                            <td>
                                <EntityCell usage={u} />
                            </td>
                            <td style={{ textAlign: "right" }}>
                                {u.baseAmountKopecks != null ? formatMoneyKopecks(u.baseAmountKopecks) : "—"}
                            </td>
                            <td style={{ textAlign: "right" }}>
                                {u.costKopecks != null ? formatMoneyKopecks(u.costKopecks) : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ScrollTable>
    );
}
